// Notice shown inside the "Add operator" confirm modal on the Dispatch Schedule,
// telling a broker/independent dispatcher what rate the operator being assigned
// will actually receive, and pointing them to Job Details to change it (DSP-190).
import React from 'react';

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { OperatorTruckIndexRecord } from '@isheepdog/operator-search';

import { startCase } from 'lodash';

import { TruckAxlesType } from '@isd/lib/firebase/db/@types';
import {
  formatJobRateWithUnit,
  getReassignedOperatorRates,
  getUserCompanyId,
} from '@isd/lib/firebase/db/helpers';
import { JobAssignmentDoc, JobDoc } from '@isd/lib/firebase/db/metaTypes';

import { replaceParamsInRoute } from '@isd/lib/helpers/functions';
import { isRateViewer } from '@isd/lib/helpers/userRoles';

import { useAuthContext } from '@/context/AuthContext';

import { reactAppJobDetailsRoute, reactAppUrl } from '@/env';

const TRUCK_AXLES_TYPES: readonly TruckAxlesType[] = [
  'tandem',
  'tri',
  'quad',
  'quint',
  'centipede',
  'trailer',
];

function isTruckAxlesType(
  value: string | null | undefined
): value is TruckAxlesType {
  return (
    typeof value === 'string' &&
    (TRUCK_AXLES_TYPES as readonly string[]).includes(value)
  );
}

interface OperatorRatesNoticeProps {
  operatorToAssign: OperatorTruckIndexRecord;
  parentJobAssignment: JobAssignmentDoc | null;
  assignmentFee: number;
  selectedJob: JobDoc;
}

/**
 * Shows the rate an operator will receive when reassigned from the Dispatch
 * Schedule, for broker/independent-dispatcher users who can view rates.
 * Rates on this screen always mirror the assigner's own rate minus their
 * assignment fee (see `getReassignedOperatorRates`) — they can't be edited
 * here, so the notice always points to Job Details for that instead.
 *
 * @param {OperatorRatesNoticeProps} props - The operator being assigned, the assigner's own job assignment, the assignment fee to apply, and the job.
 * @returns The notice panel.
 */
export default function OperatorRatesNotice({
  operatorToAssign,
  parentJobAssignment,
  assignmentFee,
  selectedJob,
}: OperatorRatesNoticeProps) {
  const { userDoc } = useAuthContext();

  const jobUnit = selectedJob.get('unit');

  if (!userDoc || !isRateViewer(userDoc)) {
    return null;
  }

  if (!parentJobAssignment || !jobUnit) {
    return <ConstraintOnlyNotice selectedJob={selectedJob} />;
  }

  const truckType = operatorToAssign?.truck?.truckType;
  const receiverTruckType = isTruckAxlesType(truckType) ? truckType : null;

  const rates = getReassignedOperatorRates({
    parentJobAssignmentDoc: parentJobAssignment,
    assignerCompanyId: getUserCompanyId(userDoc),
    receiverCompanyId: operatorToAssign.company.companyId,
    receiverTruckType,
    assignmentFee,
    jobUnit,
  });

  if (!rates.hasRates) {
    return <ConstraintOnlyNotice selectedJob={selectedJob} />;
  }

  const { resolvedTruckType, operatorRates, assignerRates, feePercentage } =
    rates;

  return (
    <div className="alert alert-soft alert-info shadow-lg" role="alert">
      <InformationCircleIcon className="size-6 shrink-0" />
      <div>
        <div className="font-bold">
          {resolvedTruckType
            ? `Operator Rate: ${formatJobRateWithUnit(operatorRates[resolvedTruckType] ?? 0, jobUnit)}`
            : 'Operator Rates'}
        </div>
        {!resolvedTruckType && (
          <ul className="list-disc pl-4">
            {Object.entries(operatorRates).map(([truckType, rate]) => (
              <li key={truckType}>
                {startCase(truckType)}:{' '}
                {formatJobRateWithUnit(rate ?? 0, jobUnit)}
              </li>
            ))}
          </ul>
        )}
        <div className="text-sm">
          {resolvedTruckType && feePercentage > 0 && (
            <div className="mb-1">
              {`Your ${formatJobRateWithUnit(assignerRates[resolvedTruckType] ?? 0, jobUnit)} rate minus your ${feePercentage}% assignment fee.`}
            </div>
          )}
          <div>
            {
              "Rates can't be changed here — to set a different rate, Manage Job Assignments from "
            }
            <JobDetailsLink selectedJob={selectedJob} />
            {' instead.'}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConstraintOnlyNotice({ selectedJob }: { selectedJob: JobDoc }) {
  return (
    <div className="alert alert-soft alert-info shadow-lg" role="alert">
      <InformationCircleIcon className="size-6 shrink-0" />
      <div>
        <div className="font-bold">{`Rates can't be changed here`}</div>
        <div className="text-sm">
          {"To set this operator's rate, Manage Job Assignments from "}
          <JobDetailsLink selectedJob={selectedJob} />
          {' instead.'}
        </div>
      </div>
    </div>
  );
}

function JobDetailsLink({ selectedJob }: { selectedJob: JobDoc }) {
  const jobDetailsUrl = `${reactAppUrl}${replaceParamsInRoute(reactAppJobDetailsRoute, { jobId: selectedJob.id })}`;
  return (
    <a
      href={jobDetailsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="link"
      // reactAppUrl resolves to the same origin whether it's the empty
      // (microfrontend) or absolute (standalone) form, so server/client
      // disagreement here is a harmless attribute string, not the URL.
      suppressHydrationWarning
    >
      Job Details
      <ArrowTopRightOnSquareIcon className="ml-0.5 inline size-3" />
    </a>
  );
}
